import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { twJoin } from 'tailwind-merge';

import useDialog from 'hooks/useDialog';
import useModal from 'hooks/useModal';

import { sendEvent } from 'lib/gtag';

import QuickSettingCard from './QuickSettingCard';

import MOBILE_APP_REDIRECTS from 'constants/mobileAppRedirects';

const DIALOGS_BY_URL_HASHES = {
  [MOBILE_APP_REDIRECTS.SETTINGS.EDIT_PASSWORD_URL_HASH]: {
    gaEventAction: 'edit_password_mobile_redirect',
    dialog: dynamic(() => import('./EditPasswordDialog')),
    modal: null,
  },
  [MOBILE_APP_REDIRECTS.SETTINGS.EDIT_PROFILE_URL_HASH]: {
    gaEventAction: 'edit_mobile_redirect',
    dialog: dynamic(() => import('./EditProfileDialog')),
    modal: null,
  },
  [MOBILE_APP_REDIRECTS.FEEDBACK]: {
    gaEventAction: 'feedback_mobile_redirect',
    dialog: null,
    modal: dynamic(() => import('components/FeedbackModal')),
  },
};

const QUICK_SETTINGS = [
  {
    buttonText: 'Editar perfil',
    dialog: dynamic(() => import('./EditProfileDialog')),
    description: 'Nome & apelido',
    gaEventAction: 'edit',
    imageProps: {
      alt: 'Pessoa alterando os seus dados no Promobit',
      src: 'changing-username.svg',
    },
  },
  {
    buttonText: 'Alterar email',
    dialog: dynamic(() => import('./EditEmailDialog')),
    description: 'Receber alertas e mensagens',
    gaEventAction: 'edit_email',
    imageProps: {
      alt: 'E-mail com notificações',
      src: 'email-inbox.svg',
    },
  },
  {
    buttonText: 'Alterar senha',
    dialog: dynamic(() => import('./EditPasswordDialog')),
    description: 'Crie uma senha forte e segura',
    gaEventAction: 'edit_password',
    imageProps: {
      alt: 'Computador seguro',
      src: 'web-security.svg',
    },
  },
];

const QuickSettings = ({ className, gaEventCategory }) => {
  const router = useRouter();
  const { showDialog } = useDialog();
  const { showModal } = useModal();

  useEffect(() => {
    const { hash } = window.location;

    if (!hash) {
      return;
    }

    const { dialog, gaEventAction, modal } = DIALOGS_BY_URL_HASHES[hash];

    if (!dialog && !modal) {
      return;
    }

    sendEvent({ action: gaEventAction, category: gaEventCategory });

    if (dialog) {
      showDialog(dialog);
      return;
    }

    showModal(modal);
  }, [router]);

  return (
    <section
      className={twJoin(
        'flex flex-col gap-y-6 md:flex-row md:gap-x-6 md:gap-y-0',
        className
      )}
    >
      {QUICK_SETTINGS.map(
        ({ buttonText, gaEventAction, description, dialog, imageProps }) => (
          <QuickSettingCard
            key={description}
            buttonText={buttonText}
            description={description}
            imageProps={imageProps}
            onClick={() => {
              sendEvent({ action: gaEventAction, category: gaEventCategory });
              showDialog(dialog);
            }}
          />
        )
      )}
    </section>
  );
};

QuickSettings.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
};

export default QuickSettings;
